import { CalendarDays } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CapabilityGate } from '../components/CapabilityGate';
import {
  apptsForDay,
  dateKeyOf,
  navCursor,
  rangeFor,
  type CalendarView,
} from '../features/appointments/agenda-adapter';
import { AppointmentCard } from '../features/appointments/AppointmentCard';
import { AppointmentCreateModal } from '../features/appointments/AppointmentCreateModal';
import {
  AppointmentDrawer,
  type AppointmentAction,
} from '../features/appointments/AppointmentDrawer';
import { CalendarKpis } from '../features/appointments/CalendarKpis';
import { DayView, MonthView, WeekView } from '../features/appointments/CalendarViews';
import { CalToolbar } from '../features/appointments/CalToolbar';
import { NoShowModal } from '../features/appointments/NoShowModal';
import { RescheduleModal } from '../features/appointments/RescheduleModal';
import { ResourceDayGrid } from '../features/appointments/ResourceDayGrid';
import { SchedulingAvailabilityPanel } from '../features/appointments/SchedulingAvailabilityPanel';
import { WaitlistPanel } from '../features/appointments/WaitlistPanel';
import {
  useListAppointmentsRange,
  useMarkNoShowAppointment,
  useRescheduleAppointment,
  useUpdateAppointment,
} from '../hooks/use-appointments';
import { useCapability } from '../hooks/use-capability';
import { useSchedulingBlocksRange, useSchedulingResources } from '../hooks/use-scheduling';
import { useToastStore } from '../stores/toast-store';

import type { AppointmentWireItem } from '../lib/api-client';
import type { JSX } from 'react';

import '../features/appointments/agenda-2b.css';

/** Toast honesto de erro de mutation (409 conflito de versão / 422 limite / outros). */
function mutationErrorToast(err: unknown, fallback: string): void {
  const msg = err instanceof Error ? err.message : '';
  const is409 = msg.includes('409') || /conflit|version/i.test(msg);
  useToastStore.getState().push({
    variant: 'error',
    title: 'Agenda',
    description: is409
      ? 'O agendamento mudou enquanto você agia — recarregando. Tente de novo.'
      : msg.length > 0
        ? msg
        : fallback,
    durationMs: 6000,
  });
}

export function AgendaPage(): JSX.Element {
  return (
    <CapabilityGate
      capability="appointments"
      fallback={
        <div className="flex flex-col h-full">
          <div className="app-pagehead">
            <div className="app-pagehead__top">
              <h1 className="app-pagehead__title">Agenda</h1>
            </div>
          </div>
          <div className="empty-state">
            <div className="empty-state__title">Sem acesso à agenda neste tenant.</div>
          </div>
        </div>
      }
    >
      <AgendaContent />
    </CapabilityGate>
  );
}

type OpenModal = { kind: 'reschedule' | 'no-show'; appt: AppointmentWireItem } | null;

function AgendaContent(): JSX.Element {
  const [view, setView] = useState<CalendarView>('week');
  const [cursorMs, setCursorMs] = useState<number>(() => Date.now());
  const [drawerAppt, setDrawerAppt] = useState<AppointmentWireItem | null>(null);
  const [modal, setModal] = useState<OpenModal>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const reschedule = useRescheduleAppointment();
  const noShow = useMarkNoShowAppointment();
  const update = useUpdateAppointment();
  // Frente B Bloco II — agenda nativa: a visão de disponibilidade + slot-picker só
  // aparece (e só busca) p/ tenants com a cap; honest-defer p/ os demais.
  const hasNativeScheduling = useCapability('scheduling-native');
  // Frente B Bloco IV — fila de espera/capacidade: painel + fetch só p/ tenants com a cap
  // (gatear a QUERY, não só o render — `feedback_gatear_fetch_nao_so_render`).
  const hasWaitlist = useCapability('scheduling-waitlist');

  // Frente B Sessão 1 — catálogo de recursos (gated; alimenta a grade recurso-cêntrica).
  const resourcesQ = useSchedulingResources(hasNativeScheduling);
  const resources = useMemo(() => resourcesQ.data?.data ?? [], [resourcesQ.data]);

  // Range é server-side por vista (D7); a vista é montada client-side.
  const range = useMemo(() => rangeFor(view, cursorMs), [view, cursorMs]);
  // Bloqueios inline na grade (cross-resource) — só a grade recurso×tempo (dia + nativo) usa.
  // Gateia o FETCH: sem `scheduling-native` OU fora da vista de dia, não busca.
  const blocksQ = useSchedulingBlocksRange(
    range.fromISO,
    range.toISO,
    hasNativeScheduling && view === 'day',
  );
  const blocks = useMemo(() => blocksQ.data?.data ?? [], [blocksQ.data]);
  // Modo nativo = grade RECURSO-cêntrica → busca TODOS os appointments do tenant no range
  // (qualquer recurso/atendente), não só os do operador logado. Modo legado mantém o
  // recorte operador-cêntrico (`assignedToUserId='me'`) — gating XOR (não competem).
  const { data, isLoading, isError, refetch } = useListAppointmentsRange(
    hasNativeScheduling
      ? { from: range.fromISO, to: range.toISO }
      : { from: range.fromISO, to: range.toISO, assignedToUserId: 'me' },
  );

  const appts: AppointmentWireItem[] = useMemo(() => data?.data ?? [], [data]);
  const now = Date.now();

  const okToast = (description: string): void => {
    useToastStore
      .getState()
      .push({ variant: 'success', title: 'Agenda', description, durationMs: 3500 });
  };

  const handleAction = (action: AppointmentAction, appt: AppointmentWireItem): void => {
    switch (action) {
      case 'reschedule':
        setDrawerAppt(null);
        setModal({ kind: 'reschedule', appt });
        break;
      case 'no-show':
        setDrawerAppt(null);
        setModal({ kind: 'no-show', appt });
        break;
      case 'complete':
        update.mutate(
          { appointmentId: appt.id, expectedVersion: appt.version, body: { status: 'completed' } },
          {
            onSuccess: () => {
              setDrawerAppt(null);
              okToast('Agendamento concluído.');
            },
            onError: (err) => mutationErrorToast(err, 'Falha ao concluir.'),
          },
        );
        break;
      case 'cancel':
        update.mutate(
          {
            appointmentId: appt.id,
            expectedVersion: appt.version,
            body: { status: 'cancelled', cancellationReason: 'Cancelado pelo atendente' },
          },
          {
            onSuccess: () => {
              setDrawerAppt(null);
              okToast('Agendamento cancelado.');
            },
            onError: (err) => mutationErrorToast(err, 'Falha ao cancelar.'),
          },
        );
        break;
    }
  };

  const confirmReschedule = (p: { newScheduledAt: string; reason?: string }): void => {
    if (modal?.kind !== 'reschedule') return;
    const appt = modal.appt;
    reschedule.mutate(
      { appointmentId: appt.id, expectedVersion: appt.version, body: p },
      {
        onSuccess: () => {
          setModal(null);
          okToast('Agendamento reagendado.');
        },
        onError: (err) => mutationErrorToast(err, 'Falha ao reagendar.'),
      },
    );
  };

  const confirmNoShow = (p: { reason: string; generatePendency: boolean }): void => {
    if (modal?.kind !== 'no-show') return;
    const appt = modal.appt;
    noShow.mutate(
      { appointmentId: appt.id, expectedVersion: appt.version, body: p },
      {
        onSuccess: (updated) => {
          setModal(null);
          okToast(
            updated.noShowGeneratedPendencyId !== null
              ? 'Falta registrada — pendência de follow-up criada na sua caixa.'
              : 'Falta registrada.',
          );
        },
        onError: (err) => mutationErrorToast(err, 'Falha ao registrar a falta.'),
      },
    );
  };

  // Lista da agenda extendida (3 dias a partir do cursor) — só nas vistas semana/dia.
  const extendedDays = useMemo(() => {
    if (view === 'month') return [];
    return [0, 1, 2]
      .map((off) => {
        const dayMs = cursorMs + off * 24 * 60 * 60 * 1000;
        const list = apptsForDay(appts, dateKeyOf(dayMs));
        return { dayMs, list };
      })
      .filter((d) => d.list.length > 0);
  }, [view, cursorMs, appts]);

  return (
    <div className="flex flex-col h-full" style={{ overflow: 'auto' }}>
      <div className="app-pagehead">
        <div className="app-pagehead__top">
          <h1 className="app-pagehead__title">Agenda</h1>
          <span className="app-pagehead__sub">Agendamentos e visitas técnicas</span>
        </div>
      </div>

      <div className="cal-page">
        <CalendarKpis appts={appts} nowMs={now} />
        <CalToolbar
          view={view}
          cursorMs={cursorMs}
          onView={setView}
          onNav={(dir) => setCursorMs((c) => navCursor(view, c, dir))}
          onToday={() => setCursorMs(Date.now())}
          onNew={() => setCreateOpen(true)}
        />

        {/* Frente B Sessão 1 — gating XOR: o painel de disponibilidade não fica inline
            competindo com a grade; vira o fluxo guardado de "Novo agendamento" (modal). */}
        {hasWaitlist && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <WaitlistPanel />
          </div>
        )}

        {isError && (
          <div className="p-6 text-center text-text-secondary text-sm">
            Não foi possível carregar a agenda.{' '}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void refetch()}>
              Tentar de novo
            </button>
          </div>
        )}
        {isLoading && (
          <div className="p-6 text-center text-text-secondary text-sm">Carregando…</div>
        )}

        {!isLoading && !isError && (
          <>
            {view === 'month' && (
              <MonthView
                cursorMs={cursorMs}
                appts={appts}
                nowMs={now}
                onPick={(dayMs) => {
                  setCursorMs(dayMs);
                  setView('day');
                }}
              />
            )}
            {view === 'week' && (
              <WeekView cursorMs={cursorMs} appts={appts} nowMs={now} onAppt={setDrawerAppt} />
            )}
            {view === 'day' &&
              (hasNativeScheduling ? (
                <ResourceDayGrid
                  resources={resources}
                  appts={appts}
                  blocks={blocks}
                  cursorMs={cursorMs}
                  onAppt={setDrawerAppt}
                />
              ) : (
                <DayView cursorMs={cursorMs} appts={appts} onAppt={setDrawerAppt} />
              ))}

            {appts.length === 0 && (
              <div className="empty-state">
                <div className="empty-state__glyph">
                  <CalendarDays />
                </div>
                <div className="empty-state__title">Nenhum agendamento neste período.</div>
                <div className="empty-state__body">Navegue o período ou crie um novo.</div>
              </div>
            )}

            {extendedDays.length > 0 && (
              <section style={{ marginTop: 'var(--space-4)' }}>
                <div
                  className="text-xs font-medium uppercase"
                  style={{
                    color: 'var(--fg-3)',
                    letterSpacing: 'var(--track-caps)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Próximos · agenda estendida
                </div>
                <div className="cal-agenda">
                  {extendedDays.map(({ dayMs, list }) => {
                    const d = new Date(dayMs);
                    return (
                      <div key={dayMs} className="cal-agenda__day">
                        <div className="cal-agenda__date">
                          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.getDay()]}
                          <strong>
                            {d.getDate()}/{(d.getMonth() + 1).toString().padStart(2, '0')}
                          </strong>
                        </div>
                        <div className="cal-agenda__events">
                          {list.map((a) => (
                            <AppointmentCard key={a.id} appt={a} onOpen={setDrawerAppt} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {drawerAppt !== null && (
        <AppointmentDrawer
          appt={drawerAppt}
          onClose={() => setDrawerAppt(null)}
          onAction={handleAction}
        />
      )}
      {modal?.kind === 'reschedule' && (
        <RescheduleModal
          open
          appt={modal.appt}
          submitting={reschedule.isPending}
          onClose={() => setModal(null)}
          onConfirm={confirmReschedule}
        />
      )}
      {modal?.kind === 'no-show' && (
        <NoShowModal
          open
          appt={modal.appt}
          submitting={noShow.isPending}
          onClose={() => setModal(null)}
          onConfirm={confirmNoShow}
        />
      )}
      {/* Frente B Sessão 1 — em modo nativo, "Novo agendamento" abre o slot-picker GUARDADO
          (serviço → horário livre → grava sob a guarda /scheduling/book), não o datepicker
          legado (que furava a capacidade). Tenant legado mantém o modal de sempre. */}
      {hasNativeScheduling ? (
        createOpen && (
          <div
            role="dialog"
            aria-label="Novo agendamento — disponibilidade"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          >
            <div
              className="w-full max-w-2xl rounded-lg p-4 shadow-xl"
              style={{ background: 'var(--bg-surface)' }}
            >
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Novo agendamento</h2>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setCreateOpen(false)}
                >
                  Fechar
                </button>
              </div>
              <SchedulingAvailabilityPanel />
            </div>
          </div>
        )
      ) : (
        <AppointmentCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
}
